import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ThemeToggle } from "../sirius/ThemeProvider";
import { LanguageToggle, useT } from "../i18n";
import { useAuth } from "../auth/AuthContext";
import { DaggryMark } from "../components/DaggryMark";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Official Chrome Web Store URL — keep here as a single source of truth.
// Used by hero CTAs, install steps, and footer.
const CWS_URL = "https://chromewebstore.google.com/detail/europilot-%E2%80%94-sas-eurobonus/lficlephbfmbhmgcpchfnpfhdpponild";

const FEATURE_KEYS = [
  { eyebrow: "01", titleKey: "landing.features.f1.title", bodyKey: "landing.features.f1.body" },
  { eyebrow: "02", titleKey: "landing.features.f2.title", bodyKey: "landing.features.f2.body" },
  { eyebrow: "03", titleKey: "landing.features.f3.title", bodyKey: "landing.features.f3.body" },
  { eyebrow: "04", titleKey: "landing.features.f4.title", bodyKey: "landing.features.f4.body" },
];

const STEP_KEYS = [
  ["01", "landing.install.s1.title", "landing.install.s1.body"],
  ["02", "landing.install.s2.title", "landing.install.s2.body"],
  ["03", "landing.install.s3.title", "landing.install.s3.body"],
];

export default function Landing() {
  const { user } = useAuth();
  const { t, lang } = useT();
  const [amexCount, setAmexCount] = useState(null);
  const [signupCount, setSignupCount] = useState(null);
  const [cadence, setCadence] = useState("monthly");   // landing pricing toggle
  const [deal, setDeal] = useState(null);              // active deal banner

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/amex/stats`).then((r) => r.json()).then((d) => setAmexCount(d?.count ?? 0)).catch(() => {});
    let timer = null;
    const loadCount = () => {
      fetch(`${BACKEND_URL}/api/recent-signups`)
        .then((r) => r.json())
        .then((d) => setSignupCount(d?.rounded ?? d?.total ?? null))
        .catch(() => {});
    };
    loadCount();
    timer = setInterval(loadCount, 60000);
    return () => { if (timer) clearInterval(timer); };
  }, []);

  // Re-fetch the active deal whenever the user flips between monthly/yearly.
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/deals/active?cadence=${cadence}`)
      .then((r) => r.json())
      .then((d) => setDeal(d?.deal || null))
      .catch(() => setDeal(null));
  }, [cadence]);

  // Pricing math — gross prices live on the server; mirrored here for display only.
  const PRICE_MONTHLY = 29;
  const PRICE_YEARLY  = 299;
  const PRICE_MONTHLY_INTRO = 19;       // intro price for the first 2 months
  const INTRO_MONTHS = 2;
  const grossPrice = cadence === "monthly" ? PRICE_MONTHLY : PRICE_YEARLY;
  const discountedPrice = deal ? Math.round(grossPrice * (1 - deal.percent_off / 100)) : grossPrice;
  const yearlyMonthEquiv = Math.round((deal && deal.applies_to !== "monthly" ? discountedPrice : PRICE_YEARLY) / 12);
  const yearlySavings = Math.round((PRICE_MONTHLY * 12 - PRICE_YEARLY) / (PRICE_MONTHLY * 12) * 100);  // ~25%
  const signupHref = `/signup?cadence=${cadence}`;
  const dealEnds = deal ? new Date(deal.ends_at) : null;

  return (
    <div className="srs" data-testid="landing-root">
      {/* Active-deal banner — coral strip across the very top */}
      {deal && (
        <Link to={signupHref} data-testid="deal-banner"
              style={{
                display: "flex", alignItems: "center", justifyContent: "center", gap: 14,
                padding: "10px 20px", background: "var(--srs-sun)", color: "#fff",
                fontSize: 13, fontWeight: 600, textDecoration: "none",
                borderBottom: "1px solid color-mix(in srgb, var(--srs-sun) 70%, #000 30%)",
                flexWrap: "wrap", textAlign: "center",
              }}>
          <span style={{ fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.18em", fontSize: 10, opacity: 0.85 }}>
            ★ {deal.name.toUpperCase()}
          </span>
          <span style={{ opacity: 0.95 }}>{deal.reason}</span>
          <span style={{ padding: "3px 9px", background: "rgba(255,255,255,0.18)", borderRadius: 999, fontSize: 11, fontFamily: "'IBM Plex Mono', monospace", letterSpacing: "0.1em", fontWeight: 700 }}>
            –{deal.percent_off}%
          </span>
          {dealEnds && (
            <span style={{ opacity: 0.75, fontSize: 11 }}>
              · {t("landing.deal.ends", { date: dealEnds.toLocaleDateString(lang === "en" ? "en-GB" : "nb-NO", { day: "numeric", month: "short" }) })}
            </span>
          )}
          <span style={{ opacity: 0.85, fontSize: 11 }}>{t("landing.deal.start_now")}</span>
        </Link>
      )}

      {/* Sticky nav */}
      <nav style={{ position: "sticky", top: 0, zIndex: 30, background: "color-mix(in srgb, var(--srs-bg) 88%, transparent)", backdropFilter: "blur(14px)", borderBottom: "1px solid var(--srs-line)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "16px 28px", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 24 }}>
          <Link to="/" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: "var(--srs-ink)" }} data-testid="brand-mark">
            <DaggryMark size={32} animate />
            <span className="srs-display" style={{ fontSize: 26, fontWeight: 500 }}>EuroPilot</span>
          </Link>
          <div className="srs-nav-links" style={{ gap: 32, fontSize: 14, color: "var(--srs-ink-dim)" }}>
            <a href="#hvordan" style={navLink} data-testid="nav-how">{t("landing.nav.how")}</a>
            <a href="#install" style={navLink} data-testid="nav-install">{t("landing.nav.install")}</a>
            <a href="#priser" style={navLink} data-testid="nav-pricing">{t("landing.nav.pricing")}</a>
            <Link to="/faq" style={navLink} data-testid="nav-faq">FAQ</Link>
            <a href="/popup-demo.html" style={navLink} data-testid="nav-popup">{t("landing.nav.popup")}</a>
          </div>
          <div className="nav-right-cluster" style={{ display: "flex", gap: 10, alignItems: "center" }}>
            <LanguageToggle />
            <ThemeToggle />
            {user ? (
              <Link to={user.role === "owner" ? "/amex" : "/account"}
                    style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--srs-ink)", textDecoration: "none", fontSize: 13, fontWeight: 600, padding: "8px 14px", borderRadius: 999, border: "1px solid var(--srs-line)", background: "var(--srs-surface)" }}
                    data-testid="nav-account">
                <span style={{ width: 8, height: 8, borderRadius: "50%", background: "var(--srs-sun)" }} />
                <span style={{ maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {user.first_name || user.name?.split(" ")[0] || user.email?.split("@")[0]}
                </span>
              </Link>
            ) : (
              <>
                <Link to="/login" className="nav-login-link" style={{ color: "var(--srs-ink-dim)", textDecoration: "none", fontSize: 13, fontWeight: 500 }} data-testid="nav-login">{t("common.login")}</Link>
                <Link to="/signup" className="nav-signup-cta" style={{ background: "var(--srs-ink)", color: "var(--srs-bg)", border: "none", padding: "10px 18px", borderRadius: 999, fontSize: 13, fontWeight: 600, cursor: "pointer", textDecoration: "none", whiteSpace: "nowrap" }} data-testid="nav-signup">
                  {t("common.start_free")}
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section style={{ maxWidth: 1240, margin: "0 auto", padding: "0 28px" }}>
        <div style={{ background: "var(--srs-hero-gradient)", border: "1px solid var(--srs-line)", borderRadius: 28, overflow: "hidden", marginTop: 32 }}>
          <div style={{ display: "grid", gridTemplateColumns: "1.1fr 1fr", gap: 48, padding: "56px 56px 80px", alignItems: "center" }} className="hero-grid">
            <div>
              <span className="srs-pill" style={{ marginBottom: 28 }} data-testid="hero-badge">
                {t("landing.hero.badge")}
              </span>
              <h1 className="srs-display" style={{ fontSize: "clamp(48px, 6.5vw, 92px)", letterSpacing: "-0.03em", lineHeight: 0.96, margin: 0 }}>
                {t("landing.hero.title.part1")}<br />
                <em style={{ color: "var(--srs-sun)", fontStyle: "italic", fontWeight: 500 }}>{t("landing.hero.title.part2")}</em><br />
                {t("landing.hero.title.part3")}
              </h1>
              <p style={{ marginTop: 28, fontSize: 18, lineHeight: 1.65, color: "var(--srs-ink-dim)", maxWidth: 480 }}>
                {t("landing.hero.sub")}
              </p>
              <div style={{ marginTop: 36, display: "flex", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                <a
                  href={CWS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="srs-cta"
                  data-testid="hero-cws-install"
                  style={{ display: "inline-flex", alignItems: "center", gap: 10 }}
                >
                  {/* Chrome logo — simplified to match brand */}
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                    <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2"/>
                    <path d="M12 2v8M21.5 8L13.5 12M2.5 14l8-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                  </svg>
                  {t("landing.hero.cta_chrome")}
                </a>
                <Link to="/signup" className="srs-cta-ghost" data-testid="download-extension-btn">
                  {t("landing.hero.cta_primary")}
                </Link>
              </div>
              <div style={{ marginTop: 24, display: "flex", gap: 20, flexWrap: "wrap", fontSize: 12, color: "var(--srs-ink-faint)" }}>
                <span>{t("landing.hero.bullet1")}</span>
                <span>{t("landing.hero.bullet2")}</span>
                <span>{t("landing.hero.bullet3")}</span>
                <span>{t("landing.hero.bullet4")}</span>
              </div>
            </div>

            {/* Hero illustration: flight arc + popup card */}
            <div style={{ position: "relative", height: 480 }}>
              <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%" }} viewBox="0 0 400 460" fill="none" aria-hidden="true">
                <path d="M0 380 Q 200 60 400 380" stroke="var(--srs-sky)" strokeWidth="2" strokeDasharray="6 8" />
                <circle cx="40" cy="358" r="6" fill="var(--srs-sun)" />
                <circle cx="360" cy="358" r="6" fill="var(--srs-ink)" />
              </svg>

              {/* floating popup preview — mirrors the real EuroPilot popup */}
              <div style={{ position: "absolute", top: 70, right: 10, width: 260, padding: 0, background: "var(--srs-surface)", borderRadius: 18, boxShadow: "var(--srs-shadow-card)", border: "1px solid var(--srs-line)", overflow: "hidden" }}>
                {/* Brand row (Daggry mark + wordmark) — matches popup header */}
                <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "12px 18px", borderBottom: "1px solid var(--srs-line)" }}>
                  <span style={{ width: 18, height: 18, borderRadius: "50%", background: "var(--srs-sun)", display: "inline-grid", placeItems: "center" }}>
                    <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff" }} />
                  </span>
                  <span className="srs-display" style={{ fontSize: 14, fontWeight: 500, letterSpacing: "-0.01em" }}>EuroPilot</span>
                  <span style={{ marginLeft: "auto", width: 6, height: 6, borderRadius: "50%", background: "var(--srs-good)", boxShadow: "0 0 6px var(--srs-good)" }} />
                </div>
                <div style={{ padding: "16px 18px 18px" }}>
                  <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.2em", color: "var(--srs-ink-dim)" }}>{t("landing.hero.popup_eyebrow")}</div>
                  <div className="srs-display" style={{ fontSize: 22, marginTop: 4, fontWeight: 500 }}>{t("landing.hero.popup_verified")}</div>
                  <div style={{ marginTop: 14, padding: 14, background: "var(--srs-sand)", borderRadius: 12 }}>
                    <div className="srs-mono" style={{ fontSize: 10, color: "var(--srs-sand-deep)", letterSpacing: "0.15em" }}>EUROBONUS</div>
                    <div className="srs-display" style={{ fontSize: 38, fontWeight: 500, color: "var(--srs-sun)", lineHeight: 1, marginTop: 4 }}>+15 p</div>
                    <div style={{ fontSize: 11, color: "var(--srs-sand-deep)", marginTop: 4 }}>{t("landing.hero.popup_per")}</div>
                  </div>
                  <div style={{ marginTop: 12, display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 11, color: "var(--srs-ink-dim)" }}>
                    <span>{t("landing.hero.popup_methods", { n: 4 })}</span>
                    <span style={{ color: "var(--srs-good)", fontWeight: 700 }}>{t("landing.hero.popup_top")}</span>
                  </div>
                </div>
              </div>

              {/* Bottom-right floating pill — represents the auto-popup
                  widget that appears on merchant pages.
                  Theme-stable colors: fixed brand-navy background +
                  white text so contrast works in BOTH light and dark
                  modes (previously used var(--srs-ink) which flips
                  to near-white in dark mode → invisible white-on-white).
                  Animation: the pill itself stays put; the plane icon
                  banks gently left/right like a cruising flight, and
                  its coral halo pulses for "live / detecting" vibe. */}
              <div
                style={{
                  position: "absolute", bottom: 16, right: 16,
                  display: "inline-flex", alignItems: "center", gap: 8,
                  padding: "10px 14px 10px 10px",
                  background: "#0B2440", color: "#fff",
                  borderRadius: 999,
                  boxShadow: "0 12px 28px rgba(11,36,64,0.45), 0 0 0 1px rgba(255,138,92,0.5)",
                  fontSize: 12, fontWeight: 600, letterSpacing: "0.02em",
                  backdropFilter: "saturate(1.2)",
                }}
                aria-hidden="true"
              >
                <span
                  style={{
                    width: 22, height: 22, borderRadius: "50%",
                    background: "var(--srs-sun)", display: "inline-grid",
                    placeItems: "center", fontSize: 12, color: "#0B2440",
                    transformOrigin: "center",
                    animation: "europilotPlaneBank 5s ease-in-out infinite",
                    willChange: "transform",
                  }}
                >
                  <span style={{ animation: "europilotPlaneGlow 5s ease-in-out infinite", borderRadius: "50%", width: "100%", height: "100%", display: "inline-grid", placeItems: "center" }}>✈</span>
                </span>
                <span>EuroPilot</span>
              </div>

              {/* trumf chip */}
              <div style={{ position: "absolute", bottom: 40, left: 20, padding: "12px 18px", background: "var(--srs-surface)", borderRadius: 16, boxShadow: "var(--srs-shadow-float)", border: "1px solid var(--srs-line)" }}>
                <div className="srs-mono" style={{ fontSize: 10, color: "var(--srs-ink-dim)", fontWeight: 600, letterSpacing: "0.15em", marginBottom: 4 }}>{t("landing.hero.trumf_label")}</div>
                <div style={{ fontWeight: 700, fontSize: 16, color: "var(--srs-sky)" }}>{t("landing.hero.trumf_value")}</div>
              </div>

              {/* method badges chip */}
              <div style={{ position: "absolute", top: 20, left: 0, padding: "8px 14px", background: "var(--srs-surface)", borderRadius: 999, boxShadow: "var(--srs-shadow-float)", border: "1px solid var(--srs-line)", fontSize: 11, fontWeight: 600, color: "var(--srs-ink)" }}>
                <span style={{ color: "var(--srs-good)" }}>✓</span> Visa · Mastercard · Vipps · Klarna
              </div>
            </div>
          </div>
        </div>

        {/* Trust strip */}
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "40px 8px 16px", flexWrap: "wrap", gap: 24 }}>
          <span className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.3em", color: "var(--srs-ink-faint)", textAlign: "center" }}>
            {t("landing.hero.trust_dekker")}
          </span>
        </div>

        {/* Privacy-safe social proof: rounded count only — no names exposed */}
        {signupCount && signupCount > 0 && (
          <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: "0 8px 32px", gap: 10 }} data-testid="signup-count">
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--srs-good)", boxShadow: "0 0 8px var(--srs-good)", animation: "pulseDot 2s ease-in-out infinite" }} />
            <span style={{ fontSize: 13, color: "var(--srs-ink-dim)" }}>
              {t("landing.hero.social_proof", { count: signupCount.toLocaleString("nb-NO") })}
            </span>
          </div>
        )}
      </section>

      {/* Features */}
      <section id="hvordan" style={{ maxWidth: 1240, margin: "0 auto", padding: "60px 28px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 48, flexWrap: "wrap", gap: 20 }}>
          <div>
            <div className="srs-eyebrow" style={{ marginBottom: 12 }}>{t("landing.what.eyebrow")}</div>
            <h2 className="srs-display" style={{ fontSize: "clamp(36px, 4.5vw, 56px)", letterSpacing: "-0.02em", margin: 0, maxWidth: 720 }}>
              {t("landing.what.title")}
            </h2>
          </div>
          <p style={{ maxWidth: 320, fontSize: 14, color: "var(--srs-ink-dim)", lineHeight: 1.6 }}>
            {t("landing.what.sub")}
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }} data-testid="features-grid">
          {FEATURE_KEYS.map((f) => (
            <div key={f.eyebrow} style={{ background: "var(--srs-surface)", border: "1px solid var(--srs-line)", borderRadius: 18, padding: 28, transition: "border-color 0.18s ease, transform 0.18s ease" }}>
              <div className="srs-mono" style={{ fontSize: 11, letterSpacing: "0.25em", color: "var(--srs-sun)", marginBottom: 18 }}>{f.eyebrow}</div>
              <h3 className="srs-display" style={{ fontSize: 24, fontWeight: 500, lineHeight: 1.2, margin: 0, color: "var(--srs-ink)" }}>{t(f.titleKey)}</h3>
              <p style={{ marginTop: 14, fontSize: 14, lineHeight: 1.6, color: "var(--srs-ink-dim)" }}>{t(f.bodyKey)}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Install steps */}
      <section id="install" style={{ maxWidth: 1240, margin: "0 auto", padding: "60px 28px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 36, flexWrap: "wrap", gap: 20 }}>
          <div>
            <div className="srs-eyebrow" style={{ marginBottom: 12 }}>{t("landing.install.eyebrow")}</div>
            <h2 className="srs-display" style={{ fontSize: "clamp(34px, 4vw, 52px)", letterSpacing: "-0.02em", margin: 0 }}>{t("landing.install.title")}</h2>
          </div>
          <a href={CWS_URL} target="_blank" rel="noopener noreferrer" className="srs-cta" data-testid="install-cws-btn">{t("landing.install.download")}</a>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 20 }} data-testid="install-steps">
          {STEP_KEYS.map(([n, titleKey, bodyKey]) => (
            <div key={n} style={{ background: "var(--srs-surface)", border: "1px solid var(--srs-line)", borderRadius: 18, padding: 28 }}>
              <div className="srs-display" style={{ fontSize: 52, fontWeight: 500, color: "var(--srs-sun)", lineHeight: 1 }}>{n}</div>
              <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--srs-ink-dim)", marginTop: 16, marginBottom: 8 }}>{t(titleKey).toUpperCase()}</div>
              <div style={{ fontSize: 14, lineHeight: 1.6, color: "var(--srs-ink-dim)" }}>{t(bodyKey)}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Pricing */}
      <section id="priser" style={{ maxWidth: 1240, margin: "0 auto", padding: "60px 28px 80px" }}>
        <div className="srs-card" style={{ padding: "48px 56px 56px", borderRadius: 28, textAlign: "center", background: "var(--srs-hero-gradient)" }}>
          <div className="srs-eyebrow" style={{ marginBottom: 18 }}>{t("landing.pricing.eyebrow")}</div>

          {/* Monthly / Yearly toggle */}
          <div role="tablist" aria-label="Faktureringssyklus" data-testid="cadence-toggle"
               style={{
                 display: "inline-flex", padding: 4, background: "var(--srs-surface)",
                 border: "1px solid var(--srs-line)", borderRadius: 999, marginBottom: 32,
                 boxShadow: "0 4px 14px rgba(11,36,64,0.06)",
               }}>
            {[
              { key: "monthly", label: t("landing.pricing.monthly_tab") },
              { key: "yearly",  label: t("landing.pricing.yearly_tab"), badge: t("landing.pricing.save_badge", { pct: yearlySavings }) },
            ].map((opt) => {
              const active = cadence === opt.key;
              return (
                <button key={opt.key} role="tab" aria-selected={active}
                        onClick={() => setCadence(opt.key)}
                        data-testid={`cadence-${opt.key}`}
                        style={{
                          padding: "9px 22px", borderRadius: 999, border: "none", cursor: "pointer",
                          background: active ? "var(--srs-ink)" : "transparent",
                          color: active ? "var(--srs-bg)" : "var(--srs-ink-dim)",
                          fontSize: 13, fontWeight: 600, fontFamily: "inherit",
                          transition: "all 0.18s ease",
                          display: "inline-flex", alignItems: "center", gap: 8,
                        }}>
                  {opt.label}
                  {opt.badge && (
                    <span style={{
                      padding: "2px 8px", fontSize: 10, fontFamily: "'IBM Plex Mono', monospace",
                      letterSpacing: "0.1em", borderRadius: 999, fontWeight: 700,
                      background: active ? "var(--srs-sun)" : "color-mix(in srgb, var(--srs-sun) 18%, transparent)",
                      color: active ? "#fff" : "var(--srs-sand-deep)",
                    }}>{opt.badge}</span>
                  )}
                </button>
              );
            })}
          </div>

          {/* Big number — for monthly we always show the intro price as
              the headline ("19 kr") with the regular 29 kr struck through.
              Deals override this with their own discount on top. */}
          <h2 className="srs-display" style={{ fontSize: "clamp(40px, 5vw, 68px)", letterSpacing: "-0.02em", margin: 0, lineHeight: 1.05 }}>
            {deal ? (
              <>
                <span style={{ color: "var(--srs-ink-faint)", textDecoration: "line-through", textDecorationThickness: 2, marginRight: 18, fontSize: "0.62em", verticalAlign: "0.18em" }} data-testid="price-original">
                  {grossPrice} kr
                </span>
                <em style={{ color: "var(--srs-sun)" }} data-testid="price-final">{discountedPrice} kr</em>
              </>
            ) : cadence === "monthly" ? (
              <>
                <span style={{ color: "var(--srs-ink-faint)", textDecoration: "line-through", textDecorationThickness: 2, marginRight: 18, fontSize: "0.62em", verticalAlign: "0.18em" }} data-testid="price-original">
                  {PRICE_MONTHLY} kr
                </span>
                <em style={{ color: "var(--srs-sun)" }} data-testid="price-final">{PRICE_MONTHLY_INTRO} kr</em>
              </>
            ) : (
              <em style={{ color: "var(--srs-sun)" }} data-testid="price-final">{grossPrice} kr</em>
            )}
            <span style={{ fontSize: "0.42em", color: "var(--srs-ink-dim)", fontWeight: 400, fontStyle: "normal", marginLeft: 12 }}>
              {cadence === "monthly" ? t("landing.pricing.per_month") : t("landing.pricing.per_year")}
            </span>
          </h2>

          {/* Intro-pricing explainer — only shown for monthly when no deal */}
          {cadence === "monthly" && !deal && (
            <div style={{ marginTop: 12, fontSize: 14, color: "var(--srs-ink-dim)" }} data-testid="intro-explainer">
              {t("landing.pricing.intro_explainer", { months: INTRO_MONTHS, regular: PRICE_MONTHLY })}
            </div>
          )}

          {cadence === "yearly" && (
            <div style={{ marginTop: 12, fontSize: 14, color: "var(--srs-ink-dim)" }} data-testid="yearly-equiv">
              {t("landing.pricing.yearly_equiv", { amount: yearlyMonthEquiv })}
            </div>
          )}

          <p style={{ marginTop: 18, fontSize: 16, color: "var(--srs-ink-dim)", maxWidth: 540, margin: "18px auto 0", lineHeight: 1.65 }}>
            {cadence === "monthly" ? t("landing.pricing.sub_monthly") : t("landing.pricing.sub_yearly")}
          </p>

          <div style={{ marginTop: 36, display: "flex", gap: 14, justifyContent: "center", flexWrap: "wrap" }}>
            <Link to={signupHref} className="srs-cta" data-testid="pricing-cta">{t("landing.pricing.cta_main")}</Link>
            <Link to="/login" className="srs-cta-ghost" data-testid="pricing-login">{t("landing.pricing.cta_login")}</Link>
          </div>
          <div style={{ marginTop: 24, fontSize: 12, color: "var(--srs-ink-faint)" }}>
            {t("landing.pricing.disclaimer")}
          </div>
        </div>

        {/* Database stat */}
        <Link to="/amex" data-testid="db-card" style={{ marginTop: 32, display: "flex", justifyContent: "space-between", alignItems: "center", padding: 24, border: "1px dashed var(--srs-line)", borderRadius: 16, textDecoration: "none", color: "var(--srs-ink)" }}>
          <div>
            <div className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--srs-ink-dim)" }}>{t("landing.db.eyebrow")}</div>
            <div style={{ marginTop: 6, fontSize: 14, color: "var(--srs-ink-dim)" }}>
              <strong className="srs-display" style={{ color: "var(--srs-sun)", fontSize: 22, marginRight: 8 }}>{amexCount ?? "—"}</strong>
              {t("landing.db.body")}
            </div>
          </div>
          <span className="srs-mono" style={{ fontSize: 10, letterSpacing: "0.25em", color: "var(--srs-ink-dim)" }}>ADMIN →</span>
        </Link>
      </section>

      {/* Footer */}
      <footer style={{ borderTop: "1px solid var(--srs-line)", padding: "32px 28px", background: "var(--srs-surface)" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 24 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <DaggryMark size={22} />
            <span className="srs-display" style={{ fontSize: 18, fontWeight: 500 }}>EuroPilot</span>
            <span className="srs-mono" style={{ fontSize: 10, color: "var(--srs-ink-faint)", letterSpacing: "0.25em", marginLeft: 8 }}>© 2026 · EUROPILOT.NO · STAVANGER</span>
          </div>
          <div style={{ display: "flex", gap: 24, fontSize: 12, color: "var(--srs-ink-dim)", flexWrap: "wrap" }}>
            <a href={CWS_URL} target="_blank" rel="noopener noreferrer" style={navLink} data-testid="footer-cws">{t("landing.footer.download")}</a>
            <a href="#hvordan" style={navLink}>{t("landing.nav.how")}</a>
            <Link to="/faq" style={navLink} data-testid="footer-faq">FAQ</Link>
            <a href="/popup-demo.html" style={navLink} data-testid="footer-popup-demo">{t("landing.nav.popup")}</a>
            <Link to="/terms" style={navLink} data-testid="footer-terms">{t("landing.footer.terms")}</Link>
            <Link to="/privacy" style={navLink} data-testid="footer-privacy">{t("landing.footer.privacy")}</Link>
          </div>
        </div>
        <div style={{ maxWidth: 1240, margin: "16px auto 0", paddingTop: 16, borderTop: "1px solid var(--srs-line-soft)", display: "flex", justifyContent: "space-between", flexWrap: "wrap", gap: 12, fontSize: 11, color: "var(--srs-ink-faint)" }}>
          <div>
            {t("landing.footer.contact_support")}: <a href="mailto:hei@europilot.no" style={{ color: "var(--srs-sun)", textDecoration: "none" }}>hei@europilot.no</a>
          </div>
          <div>
            {t("landing.footer.contact_press")}: <a href="mailto:corporate@europilot.no" style={{ color: "var(--srs-sun)", textDecoration: "none" }}>corporate@europilot.no</a>
          </div>
        </div>
      </footer>

      <style>{`
        @keyframes pulseDot {
          0%, 100% { opacity: 1; transform: scale(1); }
          50% { opacity: 0.4; transform: scale(1.4); }
        }
        /* Plane banking — gentle aviation-themed sway: the plane icon tilts
           left & right like a cruising flight. Subtle (max 6deg) so it
           reads as "in-flight / detecting" rather than wobbling. */
        @keyframes europilotPlaneBank {
          0%, 100% { transform: rotate(-6deg); }
          50%      { transform: rotate(6deg); }
        }
        /* Coral halo pulse around the plane icon — communicates "live /
           detecting payment methods on this site". Pulses 2× per banking
           cycle so the two animations stay loosely synced. */
        @keyframes europilotPlaneGlow {
          0%, 100% { box-shadow: 0 0 0 0   rgba(255,138,92,0.55); }
          50%      { box-shadow: 0 0 0 8px rgba(255,138,92,0); }
        }
        @media (prefers-reduced-motion: reduce) {
          [style*="europilotPlaneBank"], [style*="europilotPlaneGlow"] { animation: none !important; }
        }
        .srs-nav-links { display: flex; }
        @media (max-width: 900px) {
          .srs-nav-links { display: none !important; }
          .hero-grid { grid-template-columns: 1fr !important; gap: 32px !important; padding: 40px 24px 60px !important; }
          .hero-grid > div:last-child { height: 360px !important; }
        }
        @media (max-width: 640px) {
          /* tighten nav on mobile */
          nav > div { padding: 14px 18px !important; gap: 10px !important; }
          nav .srs-display { font-size: 22px !important; }
          /* right-side cluster compaction — was overflowing on phones <410px */
          .nav-right-cluster { gap: 6px !important; }
          .nav-signup-cta { padding: 8px 14px !important; font-size: 12px !important; }
          /* hero typography */
          .hero-grid h1 { font-size: clamp(38px, 11vw, 56px) !important; line-height: 0.98 !important; }
          .hero-grid .srs-pill { font-size: 10px !important; padding: 6px 12px !important; }
          .hero-grid p { font-size: 15px !important; }
          .hero-grid .srs-cta, .hero-grid .srs-cta-ghost { padding: 12px 18px !important; font-size: 13px !important; }
          /* hero illustration: smaller and pinned bottom */
          .hero-grid > div:last-child { height: 280px !important; }
          .hero-grid > div:last-child > div:nth-child(2) { width: 220px !important; padding: 16px !important; right: 0 !important; top: 40px !important; }
          .hero-grid > div:last-child > div:nth-child(3) { padding: 8px 12px !important; bottom: 16px !important; }
          .hero-grid > div:last-child > div:nth-child(4) { display: none !important; }
          /* section padding */
          section { padding-left: 18px !important; padding-right: 18px !important; }
          /* pricing card */
          .srs-card { padding: 36px 22px !important; }
          /* hero CTA stack */
          .hero-grid .srs-cta + .srs-cta-ghost { width: 100%; text-align: center; }
        }
        @media (max-width: 480px) {
          /* very narrow phones (iPhone SE, regular iPhone 14): drop the
             "Logg inn" text link entirely — users tap the brand-mark to
             reach landing then /login direct, or use the "Already have
             an account?" link inside the signup screen. */
          .nav-login-link { display: none !important; }
          /* Also hide the language toggle here — site defaults to Norwegian,
             and English-preferring users can flip the toggle from inside
             their Account → Preferences once signed up. */
          .nav-right-cluster > [data-testid="lang-toggle"] { display: none !important; }
          nav > div { padding: 12px 14px !important; gap: 8px !important; }
          nav .srs-display { font-size: 20px !important; }
          .nav-right-cluster { gap: 5px !important; }
          .nav-signup-cta { padding: 7px 12px !important; font-size: 11px !important; }
        }
      `}</style>
    </div>
  );
}

const navLink = { color: "var(--srs-ink-dim)", textDecoration: "none", transition: "color 0.18s ease", cursor: "pointer" };
